import type { Tag } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { conversationsApi } from 'apis/conversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

const MAX_SUGGESTIONS = 3;

type UseSuggestedTagsOptions = {
  /** The conversation id. */
  csid: number;
  /** The tags already applied to the conversation. */
  existingTags: readonly Tag[];
  /** Whether the conversation has any messages. */
  hasMessages: boolean;
};

/**
 * A hook that returns suggested tags for a conversation.
 *
 * Suggestions are only fetched when:
 *
 * - The feature flag is enabled
 * - No tags are already applied
 * - The conversation has messages
 */
export function useSuggestedTags({
  csid,
  existingTags,
  hasMessages,
}: UseSuggestedTagsOptions) {
  const { tagSuggestionsEnabled } = useFeatureFlag(['tag-suggestions-enabled']);
  const shouldFetch = tagSuggestionsEnabled && hasMessages;
  const { data, isFetching } = conversationsApi.useSuggestConversationTagsQuery(
    { csid },
    { skip: !shouldFetch },
  );

  const suggestions = useMemo(
    () =>
      data
        ?.filter((tag) => !existingTags.some((t) => t.name === tag.name))
        .slice(0, MAX_SUGGESTIONS) ?? [],
    [data, existingTags],
  );

  return {
    suggestions,
    isLoading: isFetching,
    isEnabled: tagSuggestionsEnabled,
  };
}
